import React, { useState, useEffect } from 'react';
import { LoadingOverlay } from './LoadingOverlay';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { api } from '../services/api';
import { toast } from '../utils/swal';
import { useAuth } from './AuthContext';
import './CheckoutForm.css';

const CheckoutForm = ({ checkoutData, minSecondsLeft, onBack, onComplete, onGoHome }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [seatsLeft, setSeatsLeft] = useState(minSecondsLeft ?? null);

  // Checkout ekranında da geri sayım devam etsin
  useEffect(() => {
    if (seatsLeft === null || seatsLeft <= 0) return;
    const interval = setInterval(() => {
      setSeatsLeft(prev => {
        if (prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []); // sadece mount'ta başlat

  const validationSchema = Yup.object({
    customerName:  Yup.string().required('Ad Soyad zorunludur').min(3, 'En az 3 karakter olmalıdır'),
    customerEmail: Yup.string().email('Geçerli bir email adresi giriniz').required('Email zorunludur'),
    customerPhone: Yup.string().matches(/^[0-9]{10}$/, 'Telefon numarası 10 haneli olmalıdır').required('Telefon numarası zorunludur'),
    notes: Yup.string()
  });

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const reservationData = {
        eventId:       checkoutData.event.id,
        venueId:       checkoutData.event.venueId,
        eventName:     checkoutData.event.name,
        eventDate:     checkoutData.event.date,
        seats: Object.values(checkoutData.selectedSeats).map(seat => ({
          seatId:      seat.seatId,
          sectionName: seat.sectionName,
          ticketType:  seat.ticketType,
          basePrice:   seat.basePrice ?? 0,
          finalPrice:  seat.finalPrice ?? 0
        })),
        customerName:    values.customerName,
        customerEmail:   values.customerEmail,
        customerPhone:   values.customerPhone,
        totalAmount:     checkoutData.totalPrice ?? 0,
        paidAmount:      0,
        paymentReceived: false,
        paymentMethod:   4,
        status:          1,
        invitationCode:  checkoutData.invitationCode || null
      };

      const reservation = await api.createReservation(reservationData);

      // 409 — koltuk çakışması: backend'den { error: "..." } gelir
      if (reservation?.error) {
        toast.error(reservation.error);
        onBack(); // koltuk seçimine geri dön, tekrar seçsin
        return;
      }

      if (!reservation?.id) {
        throw new Error('Rezervasyon oluşturulamadı');
      }

      toast.success('Rezervasyonunuz başarıyla oluşturuldu!');
      onComplete(reservation);
    } catch (error) {
      console.error('Rezervasyon hatası:', error);
      toast.error('Rezervasyon oluşturulamadı. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const getTicketTypeName = (type) => ({ 0: 'Tam Bilet', 1: 'Öğrenci', 2: 'Davetli' })[type] || 'Bilinmiyor';

  return (
    <div className="checkout-form-container">
      {loading && <LoadingOverlay message="Rezervasyon oluşturuluyor..." />}
      <div className="checkout-header">
        <button onClick={onBack} className="back-btn">← Geri</button>
        <h2><i className="fa-solid fa-clipboard-list" /> Rezervasyon Bilgileri</h2>
        {onGoHome && (
          <button onClick={onGoHome} className="home-btn">
            <i className="fa-solid fa-house" /> Ana Sayfa
          </button>
        )}
      </div>

      {/* Koltuk rezerve timer */}
      {seatsLeft !== null && seatsLeft > 0 && (
        <div className={`checkout-seat-timer ${seatsLeft < 60 ? 'urgent' : ''}`}>
          <i className="fa-solid fa-clock" />
          <span>
            Seçili koltuklar{' '}
            <strong>
              {Math.floor(seatsLeft / 60)}:{String(seatsLeft % 60).padStart(2, '0')}
            </strong>
            {' '}süre içinde rezerve tutulmaktadır. Lütfen formu tamamlayın.
          </span>
        </div>
      )}
      {seatsLeft === 0 && (
        <div className="checkout-seat-timer expired">
          <i className="fa-solid fa-triangle-exclamation" />
          <span>Koltuk rezervasyon süresi doldu. Lütfen geri dönüp tekrar seçim yapın.</span>
        </div>
      )}

      <div className="checkout-layout">
        {/* Sol — Form */}
        <div className="checkout-form-section">
          <Formik
            initialValues={{
              customerName:  user?.fullName  || '',
              customerEmail: user?.email     || '',
              customerPhone: user?.phone?.replace(/\D/g, '').slice(-10) || '',
              notes: ''
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values }) => (
              <Form>
                <div className="form-section">
                  <h3><i className="fa-solid fa-user" /> Müşteri Bilgileri</h3>

                  <div className="form-group">
                    <label htmlFor="customerName">Ad Soyad *</label>
                    <Field type="text" id="customerName" name="customerName" placeholder="Ahmet Yılmaz" className="form-input" />
                    <ErrorMessage name="customerName" component="div" className="error-message" />
                  </div>

                  <div className="form-group">
                    <label htmlFor="customerEmail">Email *</label>
                    <Field type="email" id="customerEmail" name="customerEmail" placeholder="ornek@email.com" className="form-input" />
                    <ErrorMessage name="customerEmail" component="div" className="error-message" />
                  </div>

                  <div className="form-group">
                    <label htmlFor="customerPhone">Telefon * (10 haneli)</label>
                    <Field type="text" id="customerPhone" name="customerPhone" placeholder="5551234567" maxLength="10" className="form-input" />
                    <ErrorMessage name="customerPhone" component="div" className="error-message" />
                  </div>
                </div>

                <div className="form-section">
                  <div className="payment-confirmed">
                    <i className="fa-solid fa-clipboard" /> Bu rezervasyon ücretsiz olarak kaydedilecektir.
                  </div>
                </div>

                <div className="form-section">
                  <h3><i className="fa-solid fa-pen-to-square" /> Notlar (Opsiyonel)</h3>
                  <div className="form-group">
                    <Field as="textarea" id="notes" name="notes" placeholder="Rezervasyon ile ilgili notlar..." rows="4" className="form-input" />
                  </div>
                </div>

                <button type="submit" className="submit-btn" disabled={loading}>
                  {loading
                    ? <><i className="fa-solid fa-spinner fa-spin" /> İşleniyor...</>
                    : <><i className="fa-solid fa-circle-check" /> Rezervasyonu Tamamla</>}
                </button>
              </Form>
            )}
          </Formik>
        </div>

        {/* Sağ — Özet */}
        <div className="checkout-summary">
          <h3><i className="fa-solid fa-receipt" /> Sipariş Özeti</h3>

          <div className="summary-section">
            <h4><i className="fa-solid fa-masks-theater" /> Etkinlik</h4>
            <p className="event-name">{checkoutData.event.name}</p>
            <p className="event-date">
              <i className="fa-solid fa-calendar" />{' '}
              {new Date(checkoutData.event.date).toLocaleDateString('tr-TR', {
                day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
              })}
            </p>
            <p className="venue-name"><i className="fa-solid fa-location-dot" /> {checkoutData.venue.name}</p>
          </div>

          <div className="summary-section">
            <h4><i className="fa-solid fa-ticket" /> Biletler ({Object.keys(checkoutData.selectedSeats).length} Adet)</h4>
            <div className="ticket-list">
              {Object.values(checkoutData.selectedSeats).map(seat => (
                <div key={seat.seatId} className="ticket-item">
                  <div className="ticket-info">
                    <span className="seat-id">{seat.seatId}</span>
                    <span className="ticket-type">{getTicketTypeName(seat.ticketType)}</span>
                  </div>
                  <span className="ticket-price">₺{seat.finalPrice.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="summary-total">
            <span>Toplam Tutar:</span>
            <span className="total-amount">₺{checkoutData.totalPrice.toFixed(2)}</span>
          </div>

          {/* SMS satırı kaldırıldı, email koşullu gösterim */}
          <div className="summary-note">
            <p>
              <i className="fa-solid fa-envelope" /> Ödeme onayı sonrası biletleriniz{' '}
              <strong>{checkoutData.customerEmail || 'email adresinize'}</strong>{' '}
              gönderilecektir.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutForm;